import { COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import type { ColumnsType } from 'antd/es/table';
import { getLightreachBatteryLeaseLeadIdFromOpportunity } from '../../../../helpers/lightReachLeadId';
import { lrBoOffersFromResponse } from '../../../../helpers/lightReachBatteryMonthly';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';

function normalizeFinancePartnerKey(partner: string): string {
    return (partner || '').replace(/\s/g, '').toLowerCase();
}

export function isLightreachSolarFinancePartner(opportunityDetails: any | null | undefined): boolean {
    const products = opportunityDetails?.OpportunityProduct || [];
    const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag);
    if (!solar) return false;
    const partner = String(solar.Finance_Partner_Display_Name || solar.Finance_Partner || '');
    return (
        normalizeFinancePartnerKey(partner) ===
        normalizeFinancePartnerKey(COMMON_CONSTANTS.LightreachPartnerName)
    );
}

/** Show LightReach eligible-products tooling when Solar is on the deal and LightReach is the solar finance partner. */
export function shouldShowLightreachEligibleProducts(
    opportunityDetails: any | null | undefined,
): boolean {
    const interest = opportunityDetails?.OpportunityInterest;
    return !!(
        (interest?.Solar && isLightreachSolarFinancePartner(opportunityDetails)) ||
        (interest?.Battery && isLightreachBatteryFinancePartner(opportunityDetails))
    );
}

function isLightreachBatteryFinancePartner(opportunityDetails: any | null | undefined): boolean {
    const products = opportunityDetails?.OpportunityProduct || [];
    const battery = products.find(
        (p: any) =>
            p.ActiveFlag !== false &&
            String(p.Product_Type_Name ?? p.product_Type_Name ?? '')
                .toLowerCase()
                .includes('battery'),
    );
    if (!battery) return false;
    const partner = String(battery.Finance_Partner_Display_Name || battery.Finance_Partner || '');
    const method = String(battery.Finance_Method_Name ?? battery.Finance_Method ?? '');
    return (
        normalizeFinancePartnerKey(partner) ===
            normalizeFinancePartnerKey(COMMON_CONSTANTS.LightreachPartnerName) &&
        method.toLowerCase().includes('battery lease')
    );
}

/** V1 `getEligibleProductForLightreach` — opportunity-level sync lead (solar). */
export function getLightreachSolarSyncLeadIdForEligibleProducts(od: any | null | undefined): string | null {
    if (!od) return null;
    const top =
        od.Finance_Partner_Sync_LeadID ??
        od.OpportunityDetails?.Finance_Partner_Sync_LeadID ??
        od.finance_Partner_Sync_LeadID;
    if (top != null && String(top).trim() !== '') return String(top).trim();
    return getLightreachLeadIdFromOpportunity(od);
}

export function resolveLightreachBatteryPricingAmount(
    batteryDetails: { grossSalesPrice?: unknown; billableAdderCost?: unknown; totalAdderCost?: unknown } | null | undefined,
): number {
    // V2 `BatteryQuoteDetails` already stores gross + billable adders on `grossSalesPrice`.
    const combined = Number(batteryDetails?.grossSalesPrice);
    if (Number.isFinite(combined) && combined > 0) return combined;
    const base = Number(batteryDetails?.grossSalesPrice);
    const adders =
        Number(batteryDetails?.billableAdderCost) ||
        Number(batteryDetails?.totalAdderCost) ||
        0;
    const total = (Number.isFinite(base) ? base : 0) + (Number.isFinite(adders) ? adders : 0);
    return Number.isFinite(total) && total > 0 ? total : 0;
}

export function normalizeLrBoEligibleProductRows(raw: unknown): Record<string, unknown>[] {
    return lrBoOffersFromResponse(raw).map((row) => {
        const r = row as Record<string, unknown>;
        const term = r.Term ?? r.term;
        return {
            ...r,
            Type: r.Type ?? r.type ?? 'lease',
            ProgramType: r.ProgramType ?? r.programType ?? 'energyBackup',
            Term: term,
            term,
            ProductId: r.ProductId ?? r.productId,
            EscalationRate: r.EscalationRate ?? r.escalationRate,
        };
    });
}

export function dedupeLightreachEligibleRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    const seen = new Set<string>();
    const out: Record<string, unknown>[] = [];
    for (const r of rows) {
        const key = [
            r.ProgramType,
            r.ProductId ?? r.Id ?? r.Offer_Id ?? r.offer_Id,
            r.Type,
            r.KwhRate,
            r.PpwRate,
            r.EscalationRate,
            r.Term,
            r.TotalEpcPayment,
        ]
            .map((x) => String(x ?? '').trim())
            .join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(r);
    }
    return out;
}

export type FetchMergedLightreachEligibleProductsOptions = {
    /** Gross sales + adders for GetPricingLRBO (V1 battery-detail-form). */
    batteryPricingAmount?: number | null;
    /** Rows already loaded by BatteryQuoteDetails after calculator (skip duplicate GetPricingLRBO). */
    preloadedBatteryEligibleRows?: unknown[] | null;
};

/** V1: solar `LightreachPricingByAccount` + battery `GetPricingLRBO`, merged in the modal. */
export async function fetchMergedLightreachEligibleProducts(
    opportunityId: string | number,
    opportunityDetails: any | null | undefined,
    options?: FetchMergedLightreachEligibleProductsOptions,
): Promise<Record<string, unknown>[]> {
    const merged: Record<string, unknown>[] = [];
    let lastError: unknown = null;
    const interest = opportunityDetails?.OpportunityInterest;
    const includeSolar = !!(interest?.Solar && isLightreachSolarFinancePartner(opportunityDetails));
    const includeBattery = !!(interest?.Battery && isLightreachBatteryFinancePartner(opportunityDetails));

    if (includeSolar) {
        const solarLeadId = getLightreachSolarSyncLeadIdForEligibleProducts(opportunityDetails);
        if (solarLeadId) {
            try {
                const res = await quoteServices.getLightreachPricingByAccount(opportunityId, solarLeadId);
                merged.push(...normalizeLightreachPricingRows(res));
            } catch (e: unknown) {
                const responseData = (e as { response?: { data?: { Message?: string; message?: string } } })
                    ?.response?.data;
                const apiMessage = String(responseData?.Message ?? responseData?.message ?? '').trim();
                if (apiMessage.toLowerCase() !== 'no records found') lastError = e;
            }
        }
    }

    if (includeBattery) {
        const preloaded = options?.preloadedBatteryEligibleRows;
        if (Array.isArray(preloaded) && preloaded.length > 0) {
            merged.push(...normalizeLrBoEligibleProductRows(preloaded));
        } else {
            const batteryLeadId = getLightreachBatteryLeaseLeadIdFromOpportunity(opportunityDetails);
            const amount = Number(options?.batteryPricingAmount);
            if (batteryLeadId && Number.isFinite(amount) && amount > 0) {
                try {
                    const res = await quoteServices.getEligibleProductForBatteryLightreach(
                        opportunityId,
                        batteryLeadId,
                        amount,
                    );
                    merged.push(...normalizeLrBoEligibleProductRows(res));
                } catch (e: unknown) {
                    lastError = e;
                }
            }
        }
    }

    if (merged.length === 0 && lastError) throw lastError;
    return sortLightreachEligibleRows(dedupeLightreachEligibleRows(merged));
}

export function canLoadLightreachEligibleProducts(
    opportunityDetails: any | null | undefined,
    batteryPricingAmount?: number | null,
    preloadedBatteryEligibleRows?: unknown[] | null,
): boolean {
    if (Array.isArray(preloadedBatteryEligibleRows) && preloadedBatteryEligibleRows.length > 0) {
        return true;
    }
    const interest = opportunityDetails?.OpportunityInterest;
    const includeSolar = !!(interest?.Solar && isLightreachSolarFinancePartner(opportunityDetails));
    const includeBattery = !!(interest?.Battery && isLightreachBatteryFinancePartner(opportunityDetails));
    if (includeSolar && getLightreachSolarSyncLeadIdForEligibleProducts(opportunityDetails)) return true;
    return !!(
        includeBattery &&
        getLightreachBatteryLeaseLeadIdFromOpportunity(opportunityDetails) &&
        Number(batteryPricingAmount) > 0
    );
}

export function getLightreachLeadIdFromOpportunity(od: any): string | null {
    if (!od) return null;
    const list =
        od.FinancePartnerLeadList ??
        od.financePartnerLeadListModels ??
        od.financePartnerLeadList ??
        [];
    if (Array.isArray(list)) {
        for (const row of list) {
            const fp = String(row.FinancePartnerName ?? row.Finance_Partner ?? '').toLowerCase();
            if (!fp.includes('lightreach')) continue;
            const pt = String(row.ProductTypeName ?? row.Product_Type_Name ?? '').toLowerCase();
            if (pt.includes('solar')) {
                const id = row.Finance_Partner_Sync_LeadID ?? row.FinancePartnerSyncLeadId;
                if (id) return String(id);
            }
        }
        for (const row of list) {
            const fp = String(row.FinancePartnerName ?? row.Finance_Partner ?? '').toLowerCase();
            if (fp.includes('lightreach')) {
                const id = row.Finance_Partner_Sync_LeadID ?? row.FinancePartnerSyncLeadId;
                if (id) return String(id);
            }
        }
    }
    const top = od.Finance_Partner_Sync_LeadID ?? od.OpportunityDetails?.Finance_Partner_Sync_LeadID;
    return top != null && String(top).trim() !== '' ? String(top) : null;
}

export function normalizeLightreachPricingRows(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) return raw as Record<string, unknown>[];
    if (raw && typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        if (Array.isArray(o.Data)) return o.Data as Record<string, unknown>[];
        if (Array.isArray(o.eligibleProductDetails)) return o.eligibleProductDetails as Record<string, unknown>[];
    }
    return [];
}

export function sortLightreachEligibleRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return [...rows].sort((a, b) => {
        const solarA = String(a.ProgramType ?? '').toLowerCase() === 'solar' ? 0 : 1;
        const solarB = String(b.ProgramType ?? '').toLowerCase() === 'solar' ? 0 : 1;
        if (solarA !== solarB) return solarA - solarB;
        const escA = Number(a.EscalationRate);
        const escB = Number(b.EscalationRate);
        const preferA = escA === 0.0299 ? 0 : 1;
        const preferB = escB === 0.0299 ? 0 : 1;
        if (preferA !== preferB) return preferA - preferB;
        const kwhA = Number(a.KwhRate) || 0;
        const kwhB = Number(b.KwhRate) || 0;
        if (kwhA !== kwhB) return kwhA - kwhB;
        return (Number(a.EscalationRate) || 0) - (Number(b.EscalationRate) || 0);
    });
}

function fmtUsdCell(n: unknown): string {
    const x = Number(n);
    if (Number.isNaN(x)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(x);
}

function fmtUsdCellPreservePrecision(n: unknown): string {
    if (n == null || n === '') return '—';
    const x = Number(n);
    if (Number.isNaN(x)) return String(n);
    const raw = String(n).trim();
    const fracLen = raw.includes('.') ? raw.split('.')[1]?.length ?? 0 : 0;
    const maxFractionDigits = Math.min(Math.max(fracLen, 2), 10);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: maxFractionDigits,
    }).format(x);
}

function fmtEscalationCell(v: unknown): string {
    if (v == null || v === '') return '—';
    const x = Number(v);
    if (Number.isNaN(x)) return String(v);
    if (x > 0 && x <= 1) return `${(x * 100).toFixed(2)}%`;
    return `${x}%`;
}

function lrSortTextField(field: string) {
    return (a: Record<string, unknown>, b: Record<string, unknown>) =>
        String(a[field] ?? '').localeCompare(String(b[field] ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

function lrSortNumericField(field: string) {
    return (a: Record<string, unknown>, b: Record<string, unknown>) => {
        const na = Number(a[field]);
        const nb = Number(b[field]);
        const va = Number.isFinite(na) ? na : 0;
        const vb = Number.isFinite(nb) ? nb : 0;
        return va - vb;
    };
}

export const LIGHTREACH_ELIGIBLE_COLUMNS: ColumnsType<Record<string, unknown>> = [
    {
        title: 'Type',
        dataIndex: 'Type',
        key: 'Type',
        width: 100,
        sorter: lrSortTextField('Type'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Description',
        dataIndex: 'Description',
        key: 'Description',
        ellipsis: true,
        sorter: lrSortTextField('Description'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'KWH Rate',
        dataIndex: 'KwhRate',
        key: 'KwhRate',
        width: 110,
        render: (v: unknown) => fmtUsdCellPreservePrecision(v),
        sorter: lrSortNumericField('KwhRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'PPW Rate',
        dataIndex: 'PpwRate',
        key: 'PpwRate',
        width: 110,
        render: (v: unknown) => fmtUsdCell(v),
        sorter: lrSortNumericField('PpwRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Escalation Rate',
        dataIndex: 'EscalationRate',
        key: 'EscalationRate',
        width: 120,
        render: (v: unknown) => fmtEscalationCell(v),
        sorter: lrSortNumericField('EscalationRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Program Type',
        dataIndex: 'ProgramType',
        key: 'ProgramType',
        width: 120,
        sorter: lrSortTextField('ProgramType'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Total EPC Payment',
        dataIndex: 'TotalEpcPayment',
        key: 'TotalEpcPayment',
        width: 140,
        render: (v: unknown) => fmtUsdCell(v),
        sorter: lrSortNumericField('TotalEpcPayment'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Term',
        key: 'Term',
        width: 80,
        render: (_: unknown, r: Record<string, unknown>) => {
            const t = r.Term ?? r.term;
            return t == null || t === '' ? '—' : String(t);
        },
        sorter: (a, b) => lrSortNumericField('Term')(a, b) || lrSortNumericField('term')(a, b),
        sortDirections: ['ascend', 'descend'],
    },
];
